import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Student } from '@/types/models';
import { toast } from 'sonner';

export const useOptimizedStudentsData = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [families, setFamilies] = useState<Record<string, string>>({});
  const [enrollmentCounts, setEnrollmentCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchOptimizedStudentsData = async () => {
      try {
        setLoading(true);
        
        // Single optimized query with joins to reduce database round trips
        const { data, error } = await supabase
          .from('students')
          .select(`
            *,
            families!left (
              id,
              name
            ),
            enrollments!left (
              id,
              status
            )
          `)
          .eq('is_active', true)
          .eq('enrollments.status', 'active')
          .order('last_name', { ascending: true });

        if (error) throw new Error(`Failed to fetch students data: ${error.message}`);
        
        // Process the joined data
        const familyMap: Record<string, string> = {};
        const countMap: Record<string, number> = {};
        const studentsData: Student[] = [];
        
        (data || []).forEach(student => {
          // Add to students array (deduplicate if multiple enrollments)
          if (!studentsData.find(s => s.id === student.id)) {
            const { families: familyData, enrollments, ...studentData } = student;
            studentsData.push(studentData as Student);
            
            // Add family info
            if (familyData && familyData.length > 0) {
              familyMap[familyData[0].id] = familyData[0].name;
            }
          }
          
          // Count active enrollments
          if (student.enrollments && student.enrollments.length > 0) {
            const activeEnrollments = student.enrollments.filter(e => e.status === 'active');
            countMap[student.id] = activeEnrollments.length;
          } else {
            countMap[student.id] = 0;
          }
        });
        
        setStudents(studentsData);
        setFamilies(familyMap);
        setEnrollmentCounts(countMap);
        
      } catch (error) {
        console.error('Error fetching optimized students data:', error);
        toast.error("Failed to load students data");
        
        // Fallback to original method if optimized query fails
        fallbackToOriginalMethod();
      } finally {
        setLoading(false);
      }
    };
    
    const fallbackToOriginalMethod = async () => {
      try {
        // Parallel queries as fallback
        const [studentsResult, familiesResult, enrollmentsResult] = await Promise.all([
          supabase
            .from('students')
            .select('*')
            .eq('is_active', true)
            .order('last_name', { ascending: true }),
          
          supabase
            .from('families')
            .select('id, name'),
          
          supabase
            .from('enrollments')
            .select('student_id')
            .eq('status', 'active')
        ]);

        if (studentsResult.error) throw studentsResult.error;
        if (familiesResult.error) throw familiesResult.error;
        if (enrollmentsResult.error) throw enrollmentsResult.error;
        
        // Process data
        const familyMap: Record<string, string> = {};
        (familiesResult.data || []).forEach(family => {
          familyMap[family.id] = family.name;
        });
        
        const countMap: Record<string, number> = {};
        (enrollmentsResult.data || []).forEach(enrollment => {
          const studentId = enrollment.student_id;
          if (studentId) {
            countMap[studentId] = (countMap[studentId] || 0) + 1;
          }
        });
        
        setStudents(studentsResult.data || []);
        setFamilies(familyMap);
        setEnrollmentCounts(countMap);
        
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
        toast.error("Failed to load students data");
      }
    };
    
    fetchOptimizedStudentsData();
  }, []);
  
  return {
    students,
    families,
    enrollmentCounts,
    loading,
    setStudents
  };
};
